const BASE = import.meta.env.VITE_API_BASE_URL ?? '';
const TOKEN_KEY = 'pickems.token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request(path, { method = 'GET', body } = {}) {
  const headers = { Accept: 'application/json' };
  const token = getToken();

  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(`${BASE}/api/v1${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 401) {
    setToken(null);
    throw new ApiError('Your session has expired. Please sign in again.', 401);
  }

  if (response.status === 204) return null;

  const text = await response.text();
  const payload = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new ApiError(payload?.error ?? `Request failed (${response.status})`, response.status);
  }

  return payload;
}

export const api = {
  login: (email, password) => request('/auth/login', { method: 'POST', body: { email, password } }),
  register: (payload) => request('/auth/register', { method: 'POST', body: payload }),
  me: () => request('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    request('/auth/change-password', { method: 'POST', body: { currentPassword, newPassword } }),

  seasons: () => request('/schedule/seasons'),
  weeks: (season) => request(`/schedule/seasons/${season}/weeks`),
  week: (season, week) => request(`/schedule/seasons/${season}/weeks/${week}`),
  currentWeek: () => request('/schedule/current'),

  myPicks: (season, week) => request(`/picks/${season}/${week}`),
  userPicks: (season, week, userId) => request(`/picks/${season}/${week}/user/${userId}`),
  submitPicks: (payload) => request('/picks', { method: 'PUT', body: payload }),

  weeklyLeaderboard: (season, week) => request(`/leaderboard/weekly/${season}/${week}`),
  seasonLeaderboard: (season) => request(`/leaderboard/season/${season}`),

  admin: {
    users: () => request('/admin/users'),
    createUser: (payload) => request('/admin/users', { method: 'POST', body: payload }),
    updateUser: (id, payload) => request(`/admin/users/${id}`, { method: 'PUT', body: payload }),
    deleteUser: (id) => request(`/admin/users/${id}`, { method: 'DELETE' }),
    setUserPicks: (id, payload) => request(`/admin/users/${id}/picks`, { method: 'PUT', body: payload }),
    invites: () => request('/admin/invites'),
    createInvite: (payload) => request('/admin/invites', { method: 'POST', body: payload }),
    revokeInvite: (id) => request(`/admin/invites/${id}`, { method: 'DELETE' }),
    sync: (seasonYear, weekNumber) =>
      request('/admin/sync', { method: 'POST', body: { seasonYear, weekNumber } }),
  },
};
